import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar, 
  Clock, 
  Linkedin, 
  Facebook, 
  Instagram,
  X,
  Save,
  Image,
  Settings,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdmin } from '@/context/AdminContext';
import { toast } from 'sonner';
import axios from 'axios';
import SocialMediaSetupGuide from './SocialMediaSetupGuide';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platformIcons = {
  linkedin: Linkedin,
  facebook: Facebook,
  instagram: Instagram
};

const platformColors = {
  linkedin: 'text-blue-500',
  facebook: 'text-blue-600',
  instagram: 'text-pink-500'
};

export default function AdminSocialMedia() {
  const { token } = useAdmin();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    platforms: [],
    scheduled_date: '',
    scheduled_time: '',
    status: 'scheduled',
    media_urls: []
  });

  useEffect(() => {
    fetchPosts();
  }, [token]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/social-posts?token=${token}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    try {
      if (editingPost) {
        await axios.put(`${API}/admin/social-posts/${editingPost.id}?token=${token}`, formData);
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/admin/social-posts?token=${token}`, formData);
        toast.success('Post created');
      }
      fetchPosts();
      resetForm();
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/admin/social-posts/${id}?token=${token}`);
      setPosts(posts.filter(p => p.id !== id));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      platforms: post.platforms,
      scheduled_date: post.scheduled_date?.split('T')[0] || '',
      scheduled_time: post.scheduled_time,
      status: post.status,
      media_urls: post.media_urls || []
    });
    setShowModal(true);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/admin/social-posts/${id}?token=${token}`, { status: newStatus });
      fetchPosts();
      toast.success(`Post marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      platforms: [],
      scheduled_date: '',
      scheduled_time: '',
      status: 'scheduled',
      media_urls: []
    });
  };

  const togglePlatform = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const filteredPosts = filter === 'all' 
    ? posts 
    : posts.filter(p => p.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  // Show setup guide if toggled
  if (showSetupGuide) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Social Media Setup Guide</h1>
            <p className="text-slate-400">Configure API credentials for automatic posting</p>
          </div>
          <Button 
            onClick={() => setShowSetupGuide(false)}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            Back to Scheduler
          </Button>
        </div>
        <SocialMediaSetupGuide />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="admin-social-title">Social Media Scheduler</h1>
          <p className="text-slate-400">Plan and organize your social media content</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowSetupGuide(true)}
            variant="outline"
            className="border-amber-600/50 text-amber-400 hover:bg-amber-600/20"
            data-testid="setup-guide-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            API Setup Guide
          </Button>
          <Button 
            onClick={() => setShowModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-post-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Post
          </Button>
        </div>
      </div>

      {/* Integration Notice */}
      <Card className="bg-amber-900/20 border-amber-700/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-amber-300 font-medium">Posts are saved but not automatically published</p>
              <p className="text-amber-400/70 text-sm mt-1">
                To enable automatic posting to LinkedIn, Facebook, and Instagram, you need to set up API credentials. 
                Click "API Setup Guide" above for step-by-step instructions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {['all', 'scheduled', 'posted', 'draft'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Posts Grid */}
      {filteredPosts.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <p className="text-slate-400">No posts found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className="bg-slate-800 border-slate-700 h-full" data-testid={`post-card-${index}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex gap-2">
                      {post.platforms.map(platform => {
                        const Icon = platformIcons[platform];
                        return Icon && (
                          <Icon key={platform} className={`w-5 h-5 ${platformColors[platform]}`} />
                        );
                      })}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      post.status === 'posted' ? 'bg-emerald-500/20 text-emerald-400' :
                      post.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-slate-600 text-slate-400'
                    }`}>
                      {post.status}
                    </span>
                  </div>

                  <h3 className="text-white font-medium mb-2 line-clamp-1">{post.title}</h3>
                  <p className="text-slate-400 text-sm mb-3 line-clamp-3">{post.content}</p>

                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-4">
                    <Calendar className="w-4 h-4" />
                    <span>{post.scheduled_date?.split('T')[0]}</span>
                    <Clock className="w-4 h-4 ml-2" />
                    <span>{post.scheduled_time}</span>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-slate-600 text-slate-400 hover:bg-slate-700"
                      onClick={() => handleEdit(post)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    {post.status === 'scheduled' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-600/20"
                        onClick={() => handleStatusChange(post.id, 'posted')}
                      >
                        Mark Posted
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-600/50 text-red-400 hover:bg-red-600/20"
                      onClick={() => handleDelete(post.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={resetForm}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Post title..."
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Content</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write your post content..."
                className="bg-slate-700 border-slate-600 min-h-[150px]"
                required
              />
              <p className="text-slate-500 text-xs">{formData.content.length} characters</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Platforms</Label>
              <div className="flex gap-4">
                {['linkedin', 'facebook', 'instagram'].map(platform => {
                  const Icon = platformIcons[platform];
                  return (
                    <label key={platform} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.platforms.includes(platform)}
                        onCheckedChange={() => togglePlatform(platform)}
                      />
                      <Icon className={`w-5 h-5 ${platformColors[platform]}`} />
                      <span className="text-slate-300 capitalize">{platform}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Date</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Time</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="posted">Posted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1 border-slate-600" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Save className="w-4 h-4 mr-2" />
                {editingPost ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
